import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import apis from '@src/apis';
import theme from '@src/styles/theme';
import { CircularProgress, Divider, useMediaQuery } from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  SORT_TYPES,
  VOICE_REVENUE_TRANSACTION_STATUS,
} from '@src/constants/voiceCloning';
import Card from '@src/components/Card';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import routes from '@src/constants/route';
import actions from '@src/redux/actions';
import { VOICE_REVENUE_TRANSACTION_IN_VALID } from '@src/constants/withdrawal';
import { StyledEarningsManagementContainer } from './index.style';
import EarningsHeader from './EarningsHeader';
import PromoBannerEarnings from './BannerEarnings';
import Summary from './Summary';
import ListVoiceCard from './ListVoiceCard';
import Filter from './Filter';
import Withdrawal from '../Withdrawal';
import EarningsManagementMobile from './EarningsManagementMobile';
import RequestDialogSuccess from './RequestDialogSuccess';
import CancelWithdrawalRequestDialog from './TransactionsHistory/CancelWithdrawalRequestDialog';

const EarningsManagement = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('smx'));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [communityVoices, setCommunityVoices] = useState(null);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [totalMoneyVnd, setTotalMoneyVnd] = useState(0);
  const [revenue, setRevenue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState({
    value: [null, null],
    label: 'All time',
    type: 'allTime',
  });
  const [sortType, setSortType] = useState(SORT_TYPES.RevenueDown);
  const [isOpenPopupWithdrawal, setIsOpenPopupWithdrawal] = useState(false);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [isOpenPopupRequest, setIsOpenPopupRequest] = useState(false);
  const [isOpenCancelWithdrawal, setIsOpenCancelWithdrawal] = useState(false);
  const [loadingCancelWithdrawal, setLoadingCancelWithdrawal] = useState(false);
  const history = useHistory();

  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);
  const [paidTransaction, setPaidTransaction] = useState(null);
  const [isLoadingProcessingWithdrawal, setIsLoadingProcessingWithdrawal] =
    useState(false);

  const closePopupWithdrawal = (_, reason) => {
    if (reason !== 'backdropClick') {
      setIsOpenPopupWithdrawal(false);
    }
  };

  const closePopupRequest = () => {
    setIsOpenPopupRequest(false);
  };

  const showPopupWithdrawal = () => {
    setIsOpenPopupWithdrawal(true);
  };

  const getProcessingWithdrawal = async () => {
    setIsLoadingProcessingWithdrawal(true);
    const response = await apis.voiceCloning.getProcessingWithdrawal();
    if (response.status) {
      setProcessingWithdrawal(
        response.result?.inProcessingVoiceRevenueTransaction,
      );
    }
    setIsLoadingProcessingWithdrawal(false);
  };
  const getEarningsManagement = async () => {
    setIsLoading(true);
    const startDate = selectedDate?.value[0];
    const endDate = selectedDate?.value[1];

    const response = await apis.voiceCloning.getCommunityVoiceStats({
      startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
      endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
      search: debouncedSearch,
    });
    if (response.status) {
      if (!totalMoneyVnd) {
        setTotalMoneyVnd(response.result?.summary?.totalMoneyVnd || 0);
      }
      setCommunityVoices(response.result?.communityVoices);
      setSummary(response.result?.summary);
    }
    setIsLoading(false);
  };

  const getPaidTransaction = async () => {
    const data = await apis.transactionsHistory.getWithdrawalRequest({
      offset: 0,
      limit: 1,
      status: VOICE_REVENUE_TRANSACTION_STATUS.PAID,
    });
    if (data && data.status)
      setPaidTransaction(data.result?.voiceRevenueTransactions?.[0]);
  };

  const getTotalWithdrawn = async () => {
    const response = await apis.voiceCloning.getTotalWithdrawn();
    if (response.status) {
      setTotalWithdrawn(response.result?.totalWithdrawn?.totalAmount || 0);
    }
  };

  const getRevenue = async () => {
    const response = await apis.revenue.getRevenue();
    if (response?.status) setRevenue(response?.result);
  };

  useEffect(() => {
    getPaidTransaction();
    getTotalWithdrawn();
    getProcessingWithdrawal();
    getRevenue();
  }, []);

  useEffect(() => {
    getEarningsManagement();
  }, [debouncedSearch]);

  useEffect(() => {
    const allTime =
      selectedDate?.value[0] === null && selectedDate?.value[1] === null;
    if (allTime || (selectedDate?.value[0] && selectedDate?.value[1])) {
      getEarningsManagement();
    }
  }, [selectedDate?.value[0], selectedDate?.value[1]]);

  const handleResetFilter = () => {
    setDebouncedSearch('');
    setSearch('');
    setSelectedDate({
      value: [null, null],
      label: 'All time',
      type: 'allTime',
    });
    setSortType(SORT_TYPES.RevenueDown);
  };

  const handleSuccessWithdrawal = () => {
    setIsOpenPopupRequest(true);
    getTotalWithdrawn();
    getProcessingWithdrawal();
  };

  const handleShowTransactionsHistory = () => {
    history.push(routes.TRANSACTIONS_HISTORY);
  };

  const notify = (message, severity) => {
    dispatch(actions.noti.push({ severity, message }));
  };

  const handleCancelWithdrawalRequest = async () => {
    if (processingWithdrawal?.id) {
      setLoadingCancelWithdrawal(true);
      const response = await apis.transactionsHistory.cancelWithdrawalRequest(
        processingWithdrawal?.id,
      );
      if (response?.status) {
        notify(t('cancelWithdrawalRequestSuccess'), 'success');
      } else {
        const isInvalid = response?.code === VOICE_REVENUE_TRANSACTION_IN_VALID;
        const errorMessage = isInvalid
          ? t('cancellationWithdrawalRequestFailed')
          : t('errorOccurred');
        notify(errorMessage, 'error');
      }
      getTotalWithdrawn();
      getProcessingWithdrawal();
      closePopupWithdrawal();
      setIsOpenCancelWithdrawal(false);
      setLoadingCancelWithdrawal(false);
    }
  };

  const communityVoicesView = useMemo(() => {
    const data = [...(communityVoices || [])];

    switch (sortType) {
      case SORT_TYPES.RevenueDown:
        return data.sort((a, b) => b.totalMoneyVnd - a.totalMoneyVnd);
      case SORT_TYPES.RevenueUp:
        return data.sort((a, b) => a.totalMoneyVnd - b.totalMoneyVnd);
      case SORT_TYPES.NameAZ:
        return data.sort((a, b) =>
          (a.voice?.name || '').localeCompare(b.voice?.name || ''),
        );
      case SORT_TYPES.NameZA:
        return data.sort((a, b) =>
          (b.voice?.name || '').localeCompare(a.voice?.name || ''),
        );
      default:
        return data;
    }
  }, [communityVoices, sortType]);

  const amountCommissionReceived = useMemo(() => {
    const totalMoney = revenue?.amount - totalWithdrawn;
    return totalMoney > 0 ? totalMoney : 0;
  }, [totalWithdrawn, revenue]);

  if (isMobile) {
    return <EarningsManagementMobile />;
  }

  return (
    <StyledEarningsManagementContainer>
      <Card flexDirection="column" className="earnings-management-card">
        <div className="earnings-management-wrapper">
          <EarningsHeader />
          <Divider sx={{ margin: '24px' }} />
          <div className="earnings-management-banner">
            <PromoBannerEarnings
              balance={amountCommissionReceived}
              onRequestWithdrawal={showPopupWithdrawal}
              isShowActionButton
              status={processingWithdrawal?.status}
              isLoading={isLoadingProcessingWithdrawal}
              onShowTransactionsHistory={handleShowTransactionsHistory}
            />
          </div>
          <Filter
            search={search}
            selectedDate={selectedDate}
            handleDateRangeChange={setSelectedDate}
            sortType={sortType}
            handleSortTypeChange={setSortType}
            handleResetFilter={handleResetFilter}
          />
          <Summary summaryData={summary} />
          {!isLoading ? (
            <ListVoiceCard communityVoicesData={communityVoicesView} />
          ) : (
            <div className="earnings-management-loading">
              <CircularProgress size={24} className="btn-loading" />
            </div>
          )}
        </div>
      </Card>
      <Withdrawal
        isOpenPopupWithdrawal={isOpenPopupWithdrawal}
        closePopupWithdrawal={closePopupWithdrawal}
        balance={amountCommissionReceived}
        onSuccess={handleSuccessWithdrawal}
        processingWithdrawal={processingWithdrawal}
        paidTransaction={paidTransaction}
        key={isOpenPopupWithdrawal}
        isShowDetail={Boolean(processingWithdrawal)}
        onCancelWithdrawal={() => setIsOpenCancelWithdrawal(true)}
      />
      <CancelWithdrawalRequestDialog
        open={isOpenCancelWithdrawal}
        onClose={() => setIsOpenCancelWithdrawal(false)}
        withdrawalRequestId={processingWithdrawal?.id}
        onCancelWithdrawalRequest={handleCancelWithdrawalRequest}
        loading={loadingCancelWithdrawal}
      />
      <RequestDialogSuccess
        open={isOpenPopupRequest}
        onClose={closePopupRequest}
      />
    </StyledEarningsManagementContainer>
  );
};

export default EarningsManagement;
